import { OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import {
  Component,
  DoCheck,
  inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SAVE_STALL_AFTER_MS } from '../../../core/http-context/http-timeout';
import { OverlayService } from '../../../core/services/overlay/overlay.service';
import { closeOnEscape } from '../../functions/dialog.functions';
import { SAVE_STALLED_COPY, StalledCopy } from '../../functions/load-error.functions';
import { SaveStalledDialogComponent } from '../save-stalled-dialog/save-stalled-dialog.component';

/**
 * The page-blocking progress overlay, and — when a caller opts in via {@link releasable} — the way
 * out of one that is never going to clear.
 *
 * Mutations are deliberately left untimed by `HttpTimeoutInterceptor`, because aborting the browser
 * leg of a write does not prove the server rolled it back. A stalled save therefore produces no
 * error and no state change, so `isSaving` stays true and the backdrop stays up. Bounding the
 * *overlay* rather than the *request* is the safe move until the server write is both cancellable
 * and atomic (see `SAVE_STALL_AFTER_MS`).
 *
 * Opt in per call site. The budget is **not** defaulted: `app.component` renders this same component
 * for the app-shell bootstrap spinner, where there is no page behind the overlay to release.
 *
 * Dismissing releases only the *page*; the write is still running. Every call site that opts in must
 * also disable its own submit control while the request is in flight, or a dismissal becomes a
 * second concurrent write.
 */
@Component({
  selector: 'app-progress-spinner-overlay',
  imports: [MatProgressSpinnerModule],
  templateUrl: './progress-spinner-overlay.component.html',
  styleUrls: ['./progress-spinner-overlay.component.scss'],
})
export class ProgressSpinnerOverlayComponent implements OnInit, DoCheck, OnDestroy {
  private vcRef = inject(ViewContainerRef);
  private overlayService = inject(OverlayService);
  private dialog = inject(MatDialog);

  @Input() displayProgressSpinner: boolean | null = false;

  /**
   * Opts this overlay in to being released: after `SAVE_STALL_AFTER_MS` the spinner is replaced by a
   * dismissible "taking longer than expected" dialog. `false` (the default) means an indefinite spinner.
   *
   * The budget is owned here rather than passed in, so every blocking save reports a stall at the same
   * elapsed time as a timed-out read.
   *
   * Read when the overlay starts displaying; changing it mid-request has no effect.
   */
  @Input() releasable = false;

  /**
   * Wording for the stall dialog. Defaults to the form-save copy; pass `VERIFICATION_STALLED_COPY`
   * (or another {@link StalledCopy}) for a flow where "reopen this page and check" is not the honest
   * instruction.
   */
  @Input() stalledCopy: StalledCopy = SAVE_STALLED_COPY;

  @ViewChild('progressSpinnerRef')
  public progressSpinnerRef: TemplateRef<unknown> | undefined;
  public progressSpinnerOverlayConfig: OverlayConfig | undefined;
  public overlayRef: OverlayRef | undefined;

  /** Set once the budget has elapsed; keeps the spinner down until the request resolves. */
  private stalled = false;
  private wasDisplaying = false;
  private hasChecked = false;
  private stallTimer: ReturnType<typeof setTimeout> | undefined;
  private stalledDialogRef: MatDialogRef<SaveStalledDialogComponent> | undefined;

  ngOnInit() {
    // Config for Overlay Service
    this.progressSpinnerOverlayConfig = {
      hasBackdrop: true,
      positionStrategy: this.overlayService.positionGloballyCenter(),
    };

    // Create Overlay for progress spinner
    this.overlayRef = this.overlayService.createOverlay(this.progressSpinnerOverlayConfig);
  }

  ngDoCheck() {
    const display = !!this.displayProgressSpinner;

    // Edge-triggered deliberately. ngDoCheck runs on every change-detection pass, so starting the
    // timer on the level (`if (display)`) would restart it continuously and it would never fire.
    if (display !== this.wasDisplaying) {
      this.wasDisplaying = display;

      if (!display) {
        this.resetStallState();
      } else if (!this.hasChecked && this.releasable) {
        // Already saving on the first check: the patient has reopened the page while an earlier save
        // is still running. They have already waited on it, so skip straight to the way out.
        this.stalled = true;
        this.startStallTimer(0);
      } else {
        this.startStallTimer(SAVE_STALL_AFTER_MS);
      }
    }

    this.hasChecked = true;

    if (this.overlayRef === undefined || this.progressSpinnerRef === undefined) {
      return;
    }

    const shouldShow = display && !this.stalled;

    // Based on status of displayProgressSpinner attach/detach overlay to progress spinner template
    if (shouldShow && !this.overlayRef.hasAttached()) {
      this.overlayService.attachTemplatePortal(this.overlayRef, this.progressSpinnerRef, this.vcRef);
    } else if (!shouldShow && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }
  }

  ngOnDestroy() {
    this.clearStallTimer();
    this.closeStalledDialog();

    // dispose(), not detach(): the pane lives in the CDK overlay container, outside this component's
    // DOM. A host destroyed while the overlay is up — a route change during a stalled save — would
    // otherwise strand the pane and its backdrop on top of the next page.
    this.overlayRef?.dispose();
  }

  private startStallTimer(delayMs: number) {
    this.clearStallTimer();

    if (!this.releasable) {
      return;
    }

    this.stallTimer = setTimeout(() => this.openStalledDialog(), delayMs);
  }

  private openStalledDialog() {
    this.stallTimer = undefined;
    this.stalled = true;
    this.overlayRef?.detach();

    const dialogRef = this.dialog.open<SaveStalledDialogComponent, StalledCopy>(SaveStalledDialogComponent, {
      data: this.stalledCopy,
      // disableClose blocks the backdrop click; closeOnEscape below restores Escape.
      disableClose: true,
      // A selector rather than cdkFocusInitial: CDK force-focuses a selector match, where the
      // cdkFocusInitial path first runs a layout-based tabbability check.
      autoFocus: '.save-stalled__dismiss',
      restoreFocus: this.focusReturnTarget() ?? true,
      width: '420px',
      panelClass: 'dialog',
    });
    closeOnEscape(dialogRef);

    dialogRef.afterClosed().subscribe(() => {
      if (this.stalledDialogRef === dialogRef) {
        this.stalledDialogRef = undefined;
      }
    });

    this.stalledDialogRef = dialogRef;
  }

  /**
   * Where focus goes when the dialog closes (WCAG 2.4.3). Usually not the control the patient was
   * on: the call site disables its Save while the write is in flight, and `focus()` on a disabled
   * button does nothing. Safari never focuses a clicked button at all, so focus is often on `<body>`.
   *
   * In both cases fall back to the nearest enclosing dialog when this overlay lives inside one (the
   * schedule dialog's step 5): MatDialog has hidden the page behind it from assistive tech, so `<main>`
   * would put focus outside the modal the patient is still in. Otherwise fall back to the page's
   * `<main>` landmark (`tabindex="-1"` in `app.component` and `side-nav`).
   */
  private focusReturnTarget(): HTMLElement | null {
    const active = document.activeElement as HTMLElement | null;
    const usable =
      active !== null &&
      active !== document.body &&
      active.isConnected &&
      !active.matches(':disabled') &&
      !(this.overlayRef?.overlayElement?.contains(active) ?? false);

    if (usable) {
      return active;
    }

    const host = this.vcRef.element.nativeElement as HTMLElement;
    return (
      host.closest<HTMLElement>('mat-dialog-container, [role="dialog"]') ?? document.querySelector<HTMLElement>('main')
    );
  }

  private resetStallState() {
    this.clearStallTimer();
    this.closeStalledDialog();
    this.stalled = false;
  }

  private clearStallTimer() {
    if (this.stallTimer !== undefined) {
      clearTimeout(this.stallTimer);
      this.stallTimer = undefined;
    }
  }

  private closeStalledDialog() {
    const dialogRef = this.stalledDialogRef;
    this.stalledDialogRef = undefined;
    dialogRef?.close();
  }
}
